import {
  ArrowBack as BackIcon,
  ShoppingCart as CartIcon,
  CompareArrows as CompareIcon,
  Home as HomeIcon,
  AttachMoney as MoneyIcon,
  FlashOn as PowerIcon,
} from "@mui/icons-material";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Container,
  Divider,
  Grid,
  Paper,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  Typography,
} from "@mui/material";
import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import { fetchPackageDetails } from "../../../application/actions/packageActions";
import {
  selectSelectedPackageData,
  selectSelectedPackageError,
  selectSelectedPackageLoading,
} from "../../../application/selectors/packages";

const PackageDetails = () => {
  const { id } = useParams();
  const dispatch = useDispatch();
  const navigate = useNavigate();

  // Redux State
  const packageData = useSelector(selectSelectedPackageData);
  const loading = useSelector(selectSelectedPackageLoading);
  const error = useSelector(selectSelectedPackageError);

  // Local State
  const [currentTab, setCurrentTab] = useState(0);

  // Load package details
  useEffect(() => {
    if (id) {
      dispatch(fetchPackageDetails(id));
    }
  }, [id, dispatch]);

  const handleTabChange = (event, newValue) => {
    setCurrentTab(newValue);
  };

  const handleOrderPackage = () => {
    navigate("/orders/create", {
      state: { packageId: id, packageData },
    });
  };

  const handleCompare = () => {
    navigate("/packages/compare", {
      state: { packageIds: [id] },
    });
  };

  const handleBackToList = () => {
    navigate("/packages");
  };

  if (loading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: "80vh",
        }}
      >
        <CircularProgress size={60} />
      </Box>
    );
  }

  if (error) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
        <Button startIcon={<BackIcon />} onClick={handleBackToList}>
          بازگشت به لیست پکیج‌ها
        </Button>
      </Container>
    );
  }

  if (!packageData) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Alert severity="warning" sx={{ mb: 2 }}>
          پکیج مورد نظر یافت نشد
        </Alert>
        <Button startIcon={<BackIcon />} onClick={handleBackToList}>
          بازگشت به لیست پکیج‌ها
        </Button>
      </Container>
    );
  }

  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      {/* Header */}
      <Box sx={{ mb: 3 }}>
        <Button
          startIcon={<BackIcon />}
          onClick={handleBackToList}
          sx={{ mb: 2 }}
        >
          بازگشت به لیست
        </Button>

        <Grid container spacing={3}>
          {/* Package Image */}
          <Grid item xs={12} md={5}>
            <Card>
              <Box
                component="img"
                src={packageData.imageUrl || "/default-package.jpg"}
                alt={packageData.name}
                sx={{
                  width: "100%",
                  height: 400,
                  objectFit: "cover",
                }}
              />
            </Card>
          </Grid>

          {/* Package Info */}
          <Grid item xs={12} md={7}>
            <Paper sx={{ p: 3, height: "100%" }}>
              <Typography variant="h4" gutterBottom>
                {packageData.name}
              </Typography>

              <Box sx={{ mb: 3 }}>
                <Chip
                  icon={<PowerIcon />}
                  label={`${packageData.capacity} کیلووات`}
                  color="primary"
                  sx={{ mr: 1, mb: 1 }}
                />
                <Chip
                  label={packageData.isActive ? "فعال" : "غیرفعال"}
                  color={packageData.isActive ? "success" : "default"}
                  sx={{ mr: 1, mb: 1 }}
                />
                {packageData.warrantyYears && (
                  <Chip
                    label={`${packageData.warrantyYears} سال گارانتی`}
                    variant="outlined"
                    sx={{ mb: 1 }}
                  />
                )}
              </Box>

              <Typography
                variant="body1"
                color="text.secondary"
                sx={{ mb: 3, lineHeight: 1.8 }}
              >
                {packageData.description}
              </Typography>

              <Divider sx={{ my: 3 }} />

              {/* Key Features */}
              <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid item xs={6}>
                  <Box
                    sx={{
                      p: 2,
                      backgroundColor: "primary.light",
                      borderRadius: 1,
                      textAlign: "center",
                    }}
                  >
                    <PowerIcon sx={{ fontSize: 40, color: "primary.main" }} />
                    <Typography variant="h6" sx={{ mt: 1 }}>
                      {packageData.capacity} kW
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      ظرفیت تولید
                    </Typography>
                  </Box>
                </Grid>

                <Grid item xs={6}>
                  <Box
                    sx={{
                      p: 2,
                      backgroundColor: "success.light",
                      borderRadius: 1,
                      textAlign: "center",
                    }}
                  >
                    <MoneyIcon sx={{ fontSize: 40, color: "success.main" }} />
                    <Typography variant="h6" sx={{ mt: 1 }}>
                      {packageData.estimatedROIYears || "N/A"}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      سال بازگشت سرمایه
                    </Typography>
                  </Box>
                </Grid>

                {packageData.numberOfPanels && (
                  <Grid item xs={6}>
                    <Box
                      sx={{
                        p: 2,
                        backgroundColor: "info.light",
                        borderRadius: 1,
                        textAlign: "center",
                      }}
                    >
                      <HomeIcon sx={{ fontSize: 40, color: "info.main" }} />
                      <Typography variant="h6" sx={{ mt: 1 }}>
                        {packageData.numberOfPanels}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        تعداد پنل
                      </Typography>
                    </Box>
                  </Grid>
                )}

                <Grid item xs={6}>
                  <Box
                    sx={{
                      p: 2,
                      backgroundColor: "warning.light",
                      borderRadius: 1,
                      textAlign: "center",
                    }}
                  >
                    <Typography
                      variant="h5"
                      sx={{ color: "warning.dark", fontWeight: "bold" }}
                    >
                      {packageData.totalPrice?.toLocaleString("fa-IR")}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      ریال
                    </Typography>
                  </Box>
                </Grid>
              </Grid>

              {/* Action Buttons */}
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <Button
                    fullWidth
                    variant="contained"
                    size="large"
                    startIcon={<CartIcon />}
                    onClick={handleOrderPackage}
                    disabled={!packageData.isActive}
                  >
                    سفارش این پکیج
                  </Button>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Button
                    fullWidth
                    variant="outlined"
                    size="large"
                    startIcon={<CompareIcon />}
                    onClick={handleCompare}
                  >
                    مقایسه با سایر پکیج‌ها
                  </Button>
                </Grid>
              </Grid>
            </Paper>
          </Grid>
        </Grid>
      </Box>

      {/* Tabs Section */}
      <Paper sx={{ mt: 3 }}>
        <Tabs
          value={currentTab}
          onChange={handleTabChange}
          variant="fullWidth"
          sx={{ borderBottom: 1, borderColor: "divider" }}
        >
          <Tab label="مشخصات فنی" />
          <Tab label="تجهیزات" />
          <Tab label="پیمانکار" />
        </Tabs>

        <Box sx={{ p: 3 }}>
          {/* Tab 0: Technical Specs */}
          {currentTab === 0 && (
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>
                      <strong>مشخصه</strong>
                    </TableCell>
                    <TableCell>
                      <strong>مقدار</strong>
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  <TableRow>
                    <TableCell>ظرفیت سیستم</TableCell>
                    <TableCell>{packageData.capacity} کیلووات</TableCell>
                  </TableRow>
                  {packageData.numberOfPanels && (
                    <TableRow>
                      <TableCell>تعداد پنل</TableCell>
                      <TableCell>{packageData.numberOfPanels} عدد</TableCell>
                    </TableRow>
                  )}
                  {packageData.panelWattage && (
                    <TableRow>
                      <TableCell>قدرت هر پنل</TableCell>
                      <TableCell>{packageData.panelWattage} وات</TableCell>
                    </TableRow>
                  )}
                  {packageData.inverterCapacity && (
                    <TableRow>
                      <TableCell>ظرفیت اینورتر</TableCell>
                      <TableCell>
                        {packageData.inverterCapacity} کیلووات
                      </TableCell>
                    </TableRow>
                  )}
                  {packageData.estimatedYearlyProduction && (
                    <TableRow>
                      <TableCell>تولید سالانه تخمینی</TableCell>
                      <TableCell>
                        {packageData.estimatedYearlyProduction.toLocaleString(
                          "fa-IR"
                        )}{" "}
                        کیلووات ساعت
                      </TableCell>
                    </TableRow>
                  )}
                  {packageData.warrantyYears && (
                    <TableRow>
                      <TableCell>مدت گارانتی</TableCell>
                      <TableCell>{packageData.warrantyYears} سال</TableCell>
                    </TableRow>
                  )}
                  {packageData.installationArea && (
                    <TableRow>
                      <TableCell>مساحت مورد نیاز</TableCell>
                      <TableCell>
                        {packageData.installationArea} متر مربع
                      </TableCell>
                    </TableRow>
                  )}
                  <TableRow>
                    <TableCell>قیمت کل</TableCell>
                    <TableCell>
                      <Typography variant="h6" color="primary">
                        {packageData.totalPrice?.toLocaleString("fa-IR")} ریال
                      </Typography>
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </TableContainer>
          )}

          {/* Tab 1: Equipment */}
          {currentTab === 1 && (
            <Box>
              {packageData.equipmentList &&
              packageData.equipmentList.length > 0 ? (
                <Grid container spacing={2}>
                  {packageData.equipmentList.map((equipment, index) => (
                    <Grid item xs={12} key={index}>
                      <Paper
                        variant="outlined"
                        sx={{
                          p: 2,
                          borderLeft: 4,
                          borderLeftColor: 'primary.main',
                          '&:hover': {
                            boxShadow: 3,
                            borderLeftColor: 'primary.dark'
                          }
                        }}
                      >
                        <Grid container spacing={2}>
                          {/* ردیف و نوع تجهیز */}
                          <Grid item xs={12}>
                            <Box display="flex" alignItems="center" gap={1}>
                              <Chip 
                                label={`#${index + 1}`} 
                                size="small" 
                                color="primary"
                              />
                              <Typography variant="h6" fontWeight="bold">
                                {equipment.equipmentType || equipment.name || "تجهیز"}
                              </Typography>
                            </Box>
                          </Grid>

                          {/* برند و مدل */}
                          {(equipment.brand || equipment.model) && (
                            <Grid item xs={12} sm={6} md={4}>
                              <Paper sx={{ p: 1.5, bgcolor: 'grey.50' }}>
                                <Typography variant="caption" color="text.secondary" display="block">
                                  برند و مدل
                                </Typography>
                                <Typography variant="body1" fontWeight="medium">
                                  {equipment.brand || 'نامشخص'}
                                  {equipment.model && ` - ${equipment.model}`}
                                </Typography>
                              </Paper>
                            </Grid>
                          )}

                          {/* تعداد */}
                          <Grid item xs={6} sm={3} md={2}>
                            <Paper sx={{ p: 1.5, bgcolor: 'grey.50' }}>
                              <Typography variant="caption" color="text.secondary" display="block">
                                تعداد
                              </Typography>
                              <Typography variant="h6" color="primary">
                                {equipment.quantity || "-"}
                              </Typography>
                            </Paper>
                          </Grid>

                          {/* گارانتی */}
                          {equipment.warrantyPeriod && (
                            <Grid item xs={6} sm={3} md={2}>
                              <Paper sx={{ p: 1.5, bgcolor: 'grey.50' }}>
                                <Typography variant="caption" color="text.secondary" display="block">
                                  گارانتی
                                </Typography>
                                <Typography variant="body1" fontWeight="medium">
                                  {equipment.warrantyPeriod} ماه
                                </Typography>
                              </Paper>
                            </Grid>
                          )}

                          {/* قیمت واحد */}
                          {equipment.unitPrice && (
                            <Grid item xs={6} sm={4} md={2}>
                              <Paper sx={{ p: 1.5, bgcolor: 'success.50' }}>
                                <Typography variant="caption" color="text.secondary" display="block">
                                  قیمت واحد
                                </Typography>
                                <Typography variant="body1" fontWeight="bold" color="success.main">
                                  {equipment.unitPrice.toLocaleString("fa-IR")} ریال
                                </Typography>
                              </Paper>
                            </Grid>
                          )}

                          {/* قیمت کل */}
                          {equipment.totalPrice && (
                            <Grid item xs={6} sm={4} md={2}>
                              <Paper sx={{ p: 1.5, bgcolor: 'success.100' }}>
                                <Typography variant="caption" color="text.secondary" display="block">
                                  قیمت کل
                                </Typography>
                                <Typography variant="h6" fontWeight="bold" color="success.dark">
                                  {equipment.totalPrice.toLocaleString("fa-IR")} ریال
                                </Typography>
                              </Paper>
                            </Grid>
                          )}

                          {/* توضیحات */}
                          {equipment.description && (
                            <Grid item xs={12}>
                              <Typography variant="caption" color="text.secondary" display="block" gutterBottom>
                                توضیحات:
                              </Typography>
                              <Typography variant="body2">
                                {equipment.description}
                              </Typography>
                            </Grid>
                          )}

                          {/* مشخصات فنی */}
                          {equipment.specifications && (
                            <Grid item xs={12}>
                              <Paper sx={{ p: 1.5, bgcolor: 'info.50' }}>
                                <Typography variant="caption" color="text.secondary" display="block" gutterBottom>
                                  مشخصات فنی:
                                </Typography>
                                <Typography variant="body2">
                                  {equipment.specifications}
                                </Typography>
                              </Paper>
                            </Grid>
                          )}
                        </Grid>
                      </Paper>
                    </Grid>
                  ))}
                </Grid>
              ) : (
                <Alert severity="info">اطلاعات تجهیزات در دسترس نیست</Alert>
              )}
            </Box>
          )}

          {/* Tab 2: Contractor Info */}
          {currentTab === 2 && (
            <Box>
              {packageData.contractor ? (
                <Grid container spacing={3}>
                  <Grid item xs={12} md={6}>
                    <Card>
                      <CardContent>
                        <Typography variant="h6" gutterBottom>
                          {packageData.contractor.companyName}
                        </Typography>
                        <Divider sx={{ my: 2 }} />
                        <Typography variant="body2" color="text.secondary">
                          <strong>مدیرعامل:</strong>{" "}
                          {packageData.contractor.ceoName || "-"}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          <strong>تلفن:</strong>{" "}
                          {packageData.contractor.phone || "-"}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          <strong>ایمیل:</strong>{" "}
                          {packageData.contractor.email || "-"}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          <strong>آدرس:</strong>{" "}
                          {packageData.contractor.address || "-"}
                        </Typography>
                        {packageData.contractor.rating && (
                          <Box sx={{ mt: 2 }}>
                            <Chip
                              label={`امتیاز: ${packageData.contractor.rating}/5`}
                              color="success"
                            />
                          </Box>
                        )}
                      </CardContent>
                    </Card>
                  </Grid>

                  {packageData.contractor.description && (
                    <Grid item xs={12} md={6}>
                      <Card>
                        <CardContent>
                          <Typography variant="h6" gutterBottom>
                            درباره پیمانکار
                          </Typography>
                          <Divider sx={{ my: 2 }} />
                          <Typography variant="body2" color="text.secondary">
                            {packageData.contractor.description}
                          </Typography>
                        </CardContent>
                      </Card>
                    </Grid>
                  )}
                </Grid>
              ) : (
                <Alert severity="info">اطلاعات پیمانکار در دسترس نیست</Alert>
              )}
            </Box>
          )}
        </Box>
      </Paper>
    </Container>
  );
};

export default PackageDetails;
